import type React from 'react';
import { getUserId } from '@/utils/cookies';
import { LoginType } from '@/context/LoginContext';
import type { VerifyIdTokenResult } from '@/utils/sso';
import type { ISiteContext } from '@/context/SiteContext';
import type { FollowType, FollowsInfoType } from '@/utils/following';
import type { GaContextType } from '@/context/GoogleAnalyticsContext';
import { FollowingState } from '@/components/raven/global/FollowButton/types';

export const API_FOLLOW_TOGGLE_URL = '/.api/set-user-following';
export const FOLLOW_LOGIN_OVERLAY_VARIANT = 'follow-login';

// Helper function to build the follow info URL with userId
const buildFollowInfoUrl = (
	userId: string,
	followableId: string,
	followType: FollowType,
): string => {
	return `/.api/user/${userId}/follows/${followableId}?type=${followType}`;
};

/* --- START: FetchFollowInfo --- */
export interface FetchFollowInfoParams {
	followableId: string;
	followingState: FollowingState;
	followType: FollowType;
	isFetchingAfterLogin: boolean;
	rgisCookie: null | string;
	setFollowingState: React.Dispatch<React.SetStateAction<FollowingState>>;
	setProductItemId: React.Dispatch<React.SetStateAction<null | string>>;
}
export const fetchFollowInfo = async ({
	followableId,
	followingState,
	followType,
	isFetchingAfterLogin,
	rgisCookie,
	setFollowingState,
	setProductItemId,
}: FetchFollowInfoParams): Promise<void> => {
	if (!rgisCookie) {
		setFollowingState(FollowingState.LoggedOut);
		return;
	}

	if (isFetchingAfterLogin) return;
	if (followingState !== FollowingState.Unknown) return;

	if (!followableId || !followType) {
		return;
	}

	const userId = getUserId(rgisCookie);
	if (!userId) {
		setFollowingState(FollowingState.LoggedOut);
		return;
	}

	try {
		const response = await fetch(
			buildFollowInfoUrl(userId, followableId, followType),
			{
				headers: { 'x-rgis': rgisCookie },
			},
		);

		if (!response.ok) {
			throw new Error('FollowButton response not ok.');
		}

		const resJson = await response.json();

		if (!resJson.productItemId) {
			// Not a legal item to follow in our setup.
			setFollowingState(FollowingState.Unknown);
			return;
		}

		if (!resJson.following) {
			setFollowingState(FollowingState.Naive);
		} else if (!resJson.notificationActive) {
			setFollowingState(FollowingState.Inactive);
		} else {
			setFollowingState(FollowingState.Active);
		}
		setProductItemId(resJson.productItemId);
	} catch (error) {
		setFollowingState(FollowingState.Naive);
	}
};
/* --- END: FetchFollowInfo --- */

/* --- START: HandleAfterLogin --- */
export interface HandleAfterLoginParams {
	followableId: string;
	followType: FollowType;
	sendGaEvent: GaContextType['sendGaEvent'];
	setFollowingState: React.Dispatch<React.SetStateAction<FollowingState>>;
	setIsFetchingAfterLogin: React.Dispatch<React.SetStateAction<boolean>>;
	setProductItemId: React.Dispatch<React.SetStateAction<null | string>>;
	setToast: ISiteContext['toaster']['setToast'];
}
export const handleAfterLogin = async (
	tokenResult: VerifyIdTokenResult,
	loginType: LoginType,
	{
		followableId,
		followType,
		sendGaEvent,
		setFollowingState,
		setIsFetchingAfterLogin,
		setProductItemId,
		setToast,
	}: HandleAfterLoginParams,
) => {
	// Check for loginToFollow parameter
	const url = new URL(window.location.href);
	const loginToFollow = url.searchParams.get('loginToFollow');
	const urlFollowableId = url.searchParams.get('followableId');

	// Do not proceed if loginToFollow is not defined.
	if (loginType === LoginType.EMAIL_LINK && !loginToFollow) {
		return;
	}

	if (loginType === LoginType.EMAIL_LINK && followableId !== urlFollowableId) {
		return;
	}

	if (!tokenResult.tokens.RGIS) {
		return;
	}

	const rgisToken = tokenResult.tokens.RGIS;
	const userId = getUserId(rgisToken);
	if (!userId) {
		return;
	}

	setIsFetchingAfterLogin(true);

	try {
		// Optimistic UI change for e-mail link.
		if (loginType === LoginType.EMAIL_LINK) {
			setFollowingState(FollowingState.Active);
			setToast({
				message: 'Enabled Newsletter Updates',
				type: 'success',
			});
		}

		// First fetch to get current follow state
		const followResponse = await fetch(
			buildFollowInfoUrl(userId, followableId, followType),
			{
				headers: { 'x-rgis': rgisToken },
			},
		);

		if (!followResponse.ok) {
			setFollowingState(FollowingState.Naive);
			setToast({
				message: 'Something went wrong.',
				type: 'error',
			});
			return;
		}

		const followInfo: FollowsInfoType = await followResponse.json();

		const { productItemId } = followInfo;
		if (!productItemId) {
			setProductItemId(null);
			setFollowingState(FollowingState.Unknown);
			setToast({
				message: 'Something went wrong.',
				type: 'error',
			});
			return;
		}
		setProductItemId(productItemId);

		// If already following or notifications active, we're done
		if (followInfo.following && followInfo.notificationActive) {
			setFollowingState(FollowingState.Active);
			setToast({
				message: 'Enabled Newsletter Updates',
				type: 'success',
			});
			return;
		}

		// Activate follow
		const activateResponse = await fetch(API_FOLLOW_TOGGLE_URL, {
			body: JSON.stringify({
				frequency: 'Daily',
				medium: 'Email',
				productItemId,
				productItemType: followType,
				status: 'Active',
			}),
			headers: {
				'Content-Type': 'application/json',
				'x-rgis': rgisToken,
			},
			method: 'POST',
		});

		if (!activateResponse.ok) {
			setFollowingState(FollowingState.Naive);
			setToast({
				message: 'Something went wrong.',
				type: 'error',
			});
			return;
		}

		// Non-Optimistic UI update since LoginOverlay is used.
		if (loginType === LoginType.OAUTH) {
			setFollowingState(FollowingState.Active);
			setToast({
				message: 'Enabled Newsletter Updates',
				type: 'success',
			});
		}
		sendGaEvent('follow_button_enable', {
			followableId,
			followType,
			productItemIds: productItemId ? [productItemId] : undefined,
		});
	} catch (error) {
		setToast({
			message: 'Something went wrong.',
			type: 'error',
		});
		setFollowingState(FollowingState.Naive);
	} finally {
		setIsFetchingAfterLogin(false);
		// Clean up the URL by removing any auth-related query parameters
		url.search = '';
		window.history.replaceState(
			{},
			document.title,
			window.location.pathname + window.location.hash,
		);
	}
};
/* --- END: HandleAfterLogin --- */

/* --- START: HandleClick --- */
export interface HandleClickParams {
	followableId: string;
	followingState: FollowingState;
	followType: FollowType;
	openVariant?: (variant: string) => void;
	productItemId: null | string;
	rgisCookie: null | string;
	sendGaEvent: GaContextType['sendGaEvent'];
	setFollowingState: (state: FollowingState) => void;
	setToast: ISiteContext['toaster']['setToast'];
}
export const handleClick = async ({
	followableId,
	followingState,
	followType,
	openVariant,
	productItemId,
	rgisCookie,
	sendGaEvent,
	setFollowingState,
	setToast,
}: HandleClickParams) => {
	const requestBody = {
		frequency: 'Daily',
		medium: 'Email',
		productItemId,
		productItemType: followType,
		status: 'Inactive',
	};

	switch (followingState) {
		case FollowingState.Active:
			requestBody.status = 'Inactive';
			break;
		case FollowingState.Inactive:
		case FollowingState.Naive:
			requestBody.status = 'Active';
			break;
		case FollowingState.LoggedOut:
			if (openVariant) {
				openVariant(FOLLOW_LOGIN_OVERLAY_VARIANT);
			}
			return;
		case FollowingState.Unknown:
			return;
		default:
			return;
	}

	// Optimistic UI update.
	switch (followingState) {
		case FollowingState.Active:
			setToast({ message: 'Disabled Newsletter Updates', type: 'info' });
			setFollowingState(FollowingState.Inactive);
			break;
		case FollowingState.Inactive:
		case FollowingState.Naive:
			setToast({ message: 'Enabled Newsletter Updates', type: 'success' });
			setFollowingState(FollowingState.Active);
			break;
		default:
			break;
	}

	const response = await fetch(API_FOLLOW_TOGGLE_URL, {
		body: JSON.stringify(requestBody),
		headers: {
			'Content-Type': 'application/json',
			'x-rgis': rgisCookie || '',
		},
		method: 'POST',
	});

	// Revert to previous state.
	if (!response.ok) {
		switch (followingState) {
			case FollowingState.Active:
				setFollowingState(FollowingState.Active);
				break;
			case FollowingState.Inactive:
			case FollowingState.Naive:
				setFollowingState(FollowingState.Inactive);
				break;
			default:
				break;
		}
		setToast({ message: 'Something went wrong.', type: 'error' });
		return;
	}

	// Send GA Event after a successful toggle.
	switch (followingState) {
		case FollowingState.Active:
			sendGaEvent('follow_button_disable', {
				followableId,
				followType,
				productItemIds: productItemId ? [productItemId] : undefined,
			});
			break;
		case FollowingState.Inactive:
		case FollowingState.Naive:
			sendGaEvent('follow_button_enable', {
				followableId,
				followType,
				productItemIds: productItemId ? [productItemId] : undefined,
			});
			break;
		default:
			break;
	}
};
/* --- END: HandleClick --- */
